import { orderBy, uniqBy, upperFirst } from 'lodash-es'
import { i18n } from '../../i18n/I18n'
import { Component, attr } from '../Component'
import { element } from '../CustomElement'
import { inject } from '../../services/di'
import { LocationService } from '../../services/LocationService'
import { FlyerService } from '../../services/FlyerService'
import { SeoService } from '../../services/SeoService'
import { Flyer } from '../../definitions/FlyerDefinitions'
import { Page, renderFooter } from '../flipp-page/Page'
import { UserAgentService } from '../../services/UserAgentService'
import { slugify } from '../../utils/slugify'
import { Link } from '../flipp-link/Link'
import { Store } from '../../definitions/FlyerDefinitions'

type StoresPageContentModel = [Store[], string, string]

interface StoresPageAttributes {
    location: string
}

@element('flipp-stores-page')
export class StoresPage extends Component<StoresPageAttributes, any> {
    @attr location: string = ''

    @inject locationService: LocationService
    @inject userAgent: UserAgentService
    @inject flyerService: FlyerService
    @inject seoService: SeoService

    async prepare() {
        const { city, state } = await this.getCityState()
        const postalCode = this.locationService.postalCode

        const flyersData = await this.flyerService.getFlyersDataJSON(postalCode)
        const flyers = flyersData.flyers.map((flyer) => new Flyer(flyer))
        const stores = extractUniqStoresFromFlyers(flyers)
        return [stores, city, state] as StoresPageContentModel
    }

    async getCityState() {
        if (this.props.location) {
            const splittedInCity = this.props.location.split('-')
            const state = splittedInCity.pop()
            return { city: splittedInCity.join('_'), state }
        } else {
            const location = await this.locationService.getLocationInfo()
            return {
                city: location.city_name,
                state: slugify(location.region_name),
            }
        }
    }

    renderStoreCard(store: Store): JSX.Element {
        return (
            <Link
                class="store-item"
                to={'store'}
                params={{
                    location: this.props.location,
                    name: slugify(store.name),
                }}
            >
                <div class="store-logo-container">
                    <img alt={store.name} class="store-logo" src={store.logo} />
                </div>
                <div class="store-name">{store.name}</div>
            </Link>
        )
    }

    render([stores, city]: StoresPageContentModel) {
        const cityName = city
            .split('_')
            .map((city) => upperFirst(city))
            .join(' ')
        return (
            <Page ref="page" show="flyers">
                <div class="all-stores">
                    <div class="stores-content">
                        <div class="stores-list-container">
                            <h1 class="stores-title">
                                {this.props.location
                                    ? i18n('stores_for_city', {
                                          city: cityName,
                                      })
                                    : i18n('stores')}
                            </h1>
                            <div class="stores-list">
                                {stores.length > 0 &&
                                    stores.map((store: Store) => {
                                        return (
                                            <div class="store-wrapper">
                                                {this.renderStoreCard(store)}
                                            </div>
                                        )
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
                {renderFooter(this.locationService.locale)}
            </Page>
        )
    }
}

function extractUniqStoresFromFlyers(flyers: Flyer[]): Store[] {
    const stores = uniqBy(flyers, 'merchant').map((flyer) => ({
        id: flyer.merchantId,
        name: flyer.merchant,
        logo: flyer.merchantLogo,
    }))
    return orderBy(stores, (store) => store.name?.toLowerCase(), ['asc'])
}
